<template>
  <nav ref="megaMenuElement" class="inno-mega-menu" role="navigation">
    <VcPopover
      placement="bottom"
      class="inno-mega-menu__popover"
      :aria-label="title"
      role="menu"
      @toggle="megaMenuOpened"
    >
      <template #trigger>
        <button
          type="button"
          class="inno-mega-menu__button"
          tabindex="0"
          :disabled="loading"
          @keyup.arrow-down="focusMenuItem"
        >
          <span> {{ title }} </span>

          <VcIcon
            :name="megaMenuVisible ? 'chevron-up' : 'chevron-down'"
            size="xs"
            class="ml-1 mt-1 text-[color:var(--color-primary)]"
          />
        </button>
      </template>

      <template v-if="category" #content="{ close }">
        <div class="inno-mega-menu__content z-0">
          <InnoSubcategories
            :item="category"
            :aria-label="$t('shared.layout.header.mega_menu.aria_labels.categories')"
            @close="close"
          />
        </div>
      </template>
    </VcPopover>
  </nav>
</template>

<script setup lang="ts">
import { useElementBounding, watchDebounced } from "@vueuse/core";
import { ref, computed, onMounted, useTemplateRef, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useNavigations } from "@/core/composables";
import { useCategory } from "@/shared/catalog";
import InnoSubcategories from "./inno-subcategories.vue";

interface IProps {
  title?: string;
}

interface IEmits {
  (event: "toggle"): void;
}

defineProps<IProps>();
const emit = defineEmits<IEmits>();

const MENU_PADDING_RIGHT = 20;

const calculating = ref(false);
const navRef = useTemplateRef<HTMLElement>("navElement");
const menuRef = useTemplateRef<HTMLElement>("megaMenuElement");
const visibleItemsCount = ref(1);
const megaMenuVisible = ref(false);

const { width: menuRight } = useElementBounding(menuRef);
const { catalogMenuItems } = useNavigations();
const { markActiveCategoryTree, category: _category, fetchCategory, loading } = useCategory();
const currentRoute = useRoute();
const category = computed(() => markActiveCategoryTree(_category.value, currentRoute));

// const visibleItems = computed(() => catalogMenuItems.value.slice(0, visibleItemsCount.value));

async function calculateVisibleItems() {
  calculating.value = true;

  await nextTick();

  const menuItems = (navRef?.value?.querySelectorAll("[menu-item]") as NodeListOf<HTMLElement>) || [];
  let itemsCount = 0;

  for (let i = 0; i < catalogMenuItems.value.length; i++) {
    const item = menuItems[i];

    if (!item) {
      break;
    }

    if (useElementBounding(item).right.value > menuRight.value - MENU_PADDING_RIGHT) {
      break;
    }

    itemsCount++;
  }

  visibleItemsCount.value = itemsCount;
  calculating.value = false;
}

function focusMenuItem() {
  if (!category.value || !category.value.childCategories.length) {
    return;
  }

  const item = document.querySelector(
    `[id="subcategory-${category.value?.childCategories[0].id}"] > [role="menuitem"]`,
  ) as HTMLElement;

  if (item) {
    item.focus();
  }
}

function megaMenuOpened() {
  megaMenuVisible.value = !megaMenuVisible.value;

  emit("toggle");
}

watchDebounced(
  [menuRight, () => catalogMenuItems.value],
  async () => {
    if (!menuRef.value || calculating.value) return;
    await calculateVisibleItems();
  },
  { debounce: 100, maxWait: 1000, immediate: true },
);

onMounted(() => {
  void fetchCategory({
    maxLevel: 4,
    onlyActive: true,
  });
});
</script>

<style lang="scss">
.inno-mega-menu {
  &__button {
    @apply font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-secondary-500)] mr-4;
  }

  &__wrapper {
    height: calc(100vh - 2rem);
  }

  &__popover {
    @apply flex items-stretch;
  }

  &__icon {
    @apply fill-primary size-4;
  }

  &__divider {
    @apply self-center h-5 w-px bg-secondary-200;
  }

  &__nav {
    @apply grow flex items-center gap-6;
  }

  &__link {
    @apply text-sm font-normal text-[--header-bottom-link-color] whitespace-nowrap;

    &:hover {
      @apply text-[--header-bottom-link-hover-color];
    }
  }

  &__content {
    @apply flex min-w-0 h-full bg-white relative top-[55px] left-[-140px];
  }

  .vc-popover__content {
    padding: 0 !important;
    left: unset !important;
    top: unset !important;
  }
}
</style>
